import React from "react";
import { motion } from "framer-motion";
import { useLang, UI } from "@/i18n";
import { useContent } from "@/content";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Star } from "@/components/Monogram";

export default function Experience() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const m = content && content.manifesto;
  if (!m) return null;
  const advantages = m.advantages[lang] || m.advantages.it;

  return (
    <section id="experience" className="relative py-24 sm:py-32 lg:py-40" data-testid="experience-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-10 lg:grid-cols-12 lg:gap-10 lg:px-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>{t(m.kicker)}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display mt-6 text-3xl font-light leading-tight text-[#FAF9F6] sm:text-4xl lg:text-5xl" data-testid="experience-title">
              {t(m.title)}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-8 text-sm font-light leading-relaxed text-[#A1A1AA] sm:text-base" data-testid="experience-text">
              {t(m.text)}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-5 text-sm font-light leading-relaxed text-[#A1A1AA] sm:text-base">{t(m.text2)}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-10 text-[14px] font-light tracking-[0.28em] text-[#C5A059] sm:text-[15px]" style={{ fontFamily: "var(--font-sans)" }} data-testid="experience-quote">
              {t(m.quote)}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-10">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-5 sm:gap-6">
            <Reveal delay={0.1} className="h-full sm:col-span-3">
              <div className="group relative h-full overflow-hidden">
                <motion.img
                  src={m.image}
                  alt="Cielo stellato a LED a bordo di Obsidian"
                  className="img-spotlight h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                  loading="lazy"
                  data-testid="experience-image-main"
                />
                <div className="pointer-events-none absolute inset-0 border hairline" />
                <p className="eyebrow absolute bottom-5 left-5 right-5 !text-[9px] leading-relaxed text-[#FAF9F6]/90">
                  Cielo stellato · Stargazing from inside
                </p>
              </div>
            </Reveal>
            <div className="flex flex-col justify-between gap-8 sm:col-span-2">
              <Reveal delay={0.2}>
                <div className="group relative overflow-hidden">
                  <img
                    src={m.image_secondary}
                    alt="Obsidian Private Driver nelle Dolomiti"
                    className="img-spotlight aspect-[3/4] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                    data-testid="experience-image-secondary"
                  />
                  <div className="pointer-events-none absolute inset-0 border hairline" />
                </div>
              </Reveal>
              <Reveal delay={0.26}>
                <h3 className="font-display text-xl font-light text-[#FAF9F6] sm:text-2xl">{t(m.advantages_title)}</h3>
                <ul className="mt-6 space-y-4" data-testid="experience-advantages">
                  {advantages.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] font-light leading-relaxed text-[#A1A1AA]">
                      <span className="mt-[3px] shrink-0">
                        <Star size={9} fill="#C5A059" />
                      </span>
                      {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
